import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { View, Text, ScrollView } from 'react-native';
import { Interactable, Background, MasterDetail, sectionListStyles } from 'LocalGluons';
import PropTypes from 'prop-types';
import { t } from 'Locale';
import { useStore } from 'Store';
import { Device } from 'DeviceUtils';
import { useSettingsNavigation } from 'Pages/Settings/components/SettingsNavigationContext/SettingsNavigationContext';
import { useAppSelector } from 'ReduxStore/hooks';
import { navigationStyles } from '../../Navigation/navigationOptions';
import { IconMenu } from '../../Navigation/navigationHeaderIcons';
import { getSettingsSections } from './settingsSections';
import { useNavigationPropsContext } from '../../Navigation/navigationPropsContext';
import baseStyles from './SettingsPage.jss';
import NavigationService from '../../Navigation/navigationService';
import {
    TOP_BAR_TOTAL_HEIGHT,
    MINIMISED_OFFSET,
    DETAIL_HEADER_HEIGHT
} from '../../Navigation/Menu/Menu.styles';
import { FlashList } from '@shopify/flash-list';


const SettingsPage = (props) => {
    const { navigation } = props;
    const { mCoreConfig, userData } = useStore();
    // @ts-ignore TO DO: add type during ts refactor
    const { initRoute, defaultOptionRoute = '' } = useSettingsNavigation();
    const { updateNavigationProps, navigationProps } = useNavigationPropsContext();
    const nav = NavigationService.getTopLevelNavigationInstance().getState();
    const isMinimised = useAppSelector((state) => state.webMenu.isMinimised);
    const navRef = useRef(navigation);
    const updateNavigationPropsRef = useRef(updateNavigationProps);

    useEffect(() => {
        setNavigationOptions();
    }, []);

    useEffect(() => {
        navRef.current = navigation;
    }, [navigation]);

    useEffect(() => {
        updateNavigationPropsRef.current = updateNavigationProps;
    }, [updateNavigationProps]);


    const setNavigationOptions = () => {
        const { navigation, route } = props;
        const headerTitle = route.params?.headerTitle || t('SETTINGS.SETTINGS');
        const detailHeaderTitle = navigationProps?.ActiveSettingsPageMaster || headerTitle;
        // let customOptions = {};
        // works only for phone but it's tablet/web component
        // if (detailHeaderTitle === 'SETTINGS.FAVOURITE_CHANNELS') {
        // customOptions = favoritesSettingsNavigationOptions({
        //     navigation,
        //     route,
        //     headerTitleStyle: baseStyles.favoriteHeaderTitle
        // });
        // }

        navigation.setOptions({
            headerTitle: () =>
                !Device.isWeb ? (
                    <Text
                        style={[
                            navigationStyles.headerTitleStyleNormalWeight,
                            baseStyles.headerTitleText
                        ]}
                    >
                        {t(detailHeaderTitle).toUpperCase()}
                    </Text>
                ) :
                    null,
            headerLeft: () =>
                !Device.isWeb ? <View style={baseStyles.headerLeft}><IconMenu /></View> : null,
            // ...customOptions
        });
    };

    const sections = useMemo(() =>
        getSettingsSections(userData, mCoreConfig.nativeConsentsEnabled),
        [userData, mCoreConfig.nativeConsentsEnabled]
    );

    const flattenedSections = useMemo(() =>
        sections.reduce(
            (accumulator, currentValue) => accumulator.concat(currentValue.data),
            []
        ),
        [sections]
    );

    const onChange = useCallback((selectedOption: { title: string; }) => {
        const detailHeaderTitle = navigationProps?.ActiveSettingsPageMaster || '';
        if (detailHeaderTitle !== selectedOption.title) {
            updateNavigationPropsRef.current({
                ActiveSettingsPageMaster: selectedOption.title,
            });
        }
    }, [navigationProps]);

    const renderSectionHeader = useCallback((title) => {
        return title && title !== '' ? (
            <View
                style={[
                    baseStyles.sectionHeader,
                    baseStyles.sectionHeaderTablet
                ]}
            >
                <Text
                    style={[
                        sectionListStyles.header,
                        baseStyles.sectionHeaderText
                    ]}
                >
                    {t(title).toUpperCase()}
                </Text>
            </View>
        ) : (
            <View style={baseStyles.noHeaderPadding} />
        )
    }, []);

    const renderMenuItem = useCallback(({ item, onSelect, selectedOption }) => {

        if (item?.navigationOptions?.route === initRoute) {
            onSelect(item);
        }
        return (
            <Interactable
                style={[
                    baseStyles.sectionItem,
                    selectedOption.title === item.title
                        ? baseStyles.sectionItemSelected
                        : {}
                ]}
                onPress={() => {
                    onSelect(item);
                    navRef.current.setParams({ location: item.navigationOptions.route });
                }}
                testID={item.title}
            >
                <Text style={baseStyles.sectionItemText}>
                    {t(item.title)}
                </Text>
            </Interactable>
        );
    }, [initRoute]);

    function convertSectionListToFlashListData(sections: any[]) {
        const result = [];
        sections.forEach(section => {
            result.push(section.title);
            section.data.forEach(item => {
                result.push(item);
            });
        });
        return result;
    }

    const convertedData = useMemo(() => convertSectionListToFlashListData(sections), [sections]);

    const menuRedender = useCallback(({ onSelect, selectedOption }) => {
        return (
            <View style={baseStyles.wrapper}>
                <ScrollView>
                    <FlashList
                        data={convertedData}
                        extraData={selectedOption}
                        style={baseStyles.sectionList}
                        contentContainerStyle={baseStyles.contentContainer}
                        renderItem={({ item }) => {
                            if (typeof item === "string") {
                                return renderSectionHeader(item)
                            } else {
                                return renderMenuItem({ item, onSelect, selectedOption });
                            }
                        }}
                        getItemType={(item) => {
                            return typeof item === "string" ? "sectionHeader" : "row";
                        }}
                        estimatedItemSize={47.5}
                        keyExtractor={(item, index) =>
                            typeof item === 'string' ? `section-${item}` : `item-${item.title}-${index}`
                        }

                    />
                </ScrollView>
            </View>
        )
    }, [sections, renderMenuItem, renderSectionHeader]);

    const renderDetail = useCallback(({ selectedOption }) => (
        <View style={baseStyles.contentDetail}>{selectedOption.page}</View>
    ), []);



    return (
        <Background>
            <View style={[{ flex: 1 }, Device.isWeb &&
            {
                width: 1280,
                alignSelf: 'center',
                marginTop: isMinimised
                    ? TOP_BAR_TOTAL_HEIGHT - MINIMISED_OFFSET
                    : TOP_BAR_TOTAL_HEIGHT - DETAIL_HEADER_HEIGHT,
                transition: 'margin .16s linear'
            }]}>
                <MasterDetail
                    navigation={navigation}
                    forceLandscapeMode
                    defaultOptionRoute={defaultOptionRoute}
                    options={flattenedSections}
                    onChange={(selectedOption) => onChange(selectedOption)}
                    renderWrapper={menuRedender}
                    renderDetail={renderDetail}
                />
            </View>
        </Background>
    );
};

SettingsPage.propTypes = {
    navigation: PropTypes.shape({
        navigate: PropTypes.func.isRequired
    }).isRequired,
};

// it is no longer class; use hooks
// const wrappedSettings = withSettingsNavigation(withStore(SettingsPage));

export default SettingsPage;
